import { Checkbox, Divider, Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { clone } from 'lodash-es';
import { compareAsc, differenceInMonths, differenceInQuarters, differenceInYears } from 'date-fns';
import { GridApi, GridReadyEvent } from 'ag-grid-community';
import { useAtom, useAtomValue } from 'jotai';
import { ConfirmDialog } from '../../../../../components/Dialog/ConfirmDialog/ConfirmDialog';
import { GenericFallbacksWrapper } from '../../../../../components/Fallback/GenericFallbacksWrapper';
import { AgTable } from '../../../../../components/AgTable/AgTable';
import { sanitizeAsFileName } from '../../../../../util/stringUtils';
import { getEachDateByPeriodAndFrequency } from '../../../utils/financialUtils';
import { allCompanyFinancialsState } from '../../../state/CompanyFinancialsState';
import {
  selectedEndDateFinancialsState,
  selectedFrequencyFinancialsState,
} from '../../FinancialsDatePickers/useFinancialsDateSettings';
import { selectedCompanyIdProfileAtom } from '../../../state/UIStateJ';
import { companyStateJ } from '../../../../../services/state/CompanyStateJ';
import { KpiPeriod } from '../../../../../data-models/company-financials.data-model';
import { useFinancialsExportColumnDefs } from '../export/useFinancialsExportColumnDefs';
import { useFinancialsExportData } from '../export/useFinancialsExportData';
import { SelectPeriodDropdown } from './SelectPeriodDropdown/SelectPeriodDropdown';
import { useSelectPeriodDropdown } from './SelectPeriodDropdown/useSelectPeriodDropdown';
import { ToggleFrequencyButtons } from './ToggleFrequencyButtons';
import { useFinancialsSortState } from './financialsSortStateJ';

type CompanyProfileFinancialsExportProps = {
  open: boolean;
  onClose: () => void;
};

export function CompanyProfileFinancialsExportModal({ open, onClose }: CompanyProfileFinancialsExportProps) {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const company = useAtomValue(companyStateJ(companyId))!;
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [frequency, setFrequency] = useAtom(selectedFrequencyFinancialsState);
  const [checkedByPeriod, setCheckedByPeriod] = useState(true);
  const financials = useAtomValue(allCompanyFinancialsState(companyId));
  const [checkedByAllHistorical, setCheckedByAllHistorical] = useState(false);
  const { totalSelectedPeriods, selectedAmountOfPeriodsIdx, onChangeSelectedAmountOfPeriods } =
    useSelectPeriodDropdown(frequency!);
  const fye = useAtomValue(companyStateJ(companyId))?.fye ?? 12;

  const oldestCompanyFinancialsDate = useMemo(() => {
    if (!financials?.length) {
      return new Date();
    }
    const sortedFinancials = clone(financials).sort((a, b) => compareAsc(new Date(a.date), new Date(b.date)));

    return new Date(sortedFinancials[0].date);
  }, [financials]);

  let nPeriods = totalSelectedPeriods;
  if (checkedByAllHistorical) {
    switch (frequency) {
      case KpiPeriod.month: {
        nPeriods = differenceInMonths(new Date(), oldestCompanyFinancialsDate) + 1;
        break;
      }
      case KpiPeriod.quarter: {
        nPeriods = differenceInQuarters(new Date(), oldestCompanyFinancialsDate) + 1;
        break;
      }
      case KpiPeriod.year: {
        nPeriods = differenceInYears(new Date(), oldestCompanyFinancialsDate) + 1;
        break;
      }
    }
  }
  const selectedEndDate = useAtomValue(selectedEndDateFinancialsState);

  const allPeriods = useMemo(
    () =>
      getEachDateByPeriodAndFrequency({
        totalSelectedPeriods: nPeriods,
        frequency: frequency!,
        fye,
        endDate: selectedEndDate!,
      }),
    [nPeriods, frequency, fye, selectedEndDate]
  );

  const getRowData = useFinancialsExportData();
  const rowData = useMemo(() => getRowData(frequency!), [frequency, getRowData]);

  const { sortState } = useFinancialsSortState();
  const { columnDefs, defaultExportParams } = useFinancialsExportColumnDefs({
    allPeriods: allPeriods.dates,
    frequency: frequency!,
    company,
    sortState,
  });

  const onChangeSelectedPeriod = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckedByAllHistorical(false);
    setCheckedByPeriod(event.target.checked);
  }, []);

  const onChangeSelectedAllHistorical = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckedByPeriod(false);
    setCheckedByAllHistorical(event.target.checked);
  }, []);

  const onConfirmExport = useCallback(() => {
    if (!gridApi) {
      return;
    }
    // AG Grid v35's exportDataAsExcel() defers the row read to a later tick (via the export
    // overlay), but onClose() unmounts this hidden grid synchronously — so the deferred read
    // sees a destroyed model and exports headers only (FD-3443). getDataAsExcel() reads
    // synchronously, so build the download ourselves, then close.
    const contents = gridApi.getDataAsExcel();
    if (contents instanceof Blob) {
      const url = URL.createObjectURL(contents);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${sanitizeAsFileName(`${company.name}_financials`)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
    onClose();
  }, [gridApi, company.name, onClose]);

  const isACheckboxChecked = !checkedByPeriod && !checkedByAllHistorical;

  const onGridReady = useCallback((e: GridReadyEvent) => {
    setGridApi(e.api);
  }, []);

  return (
    <GenericFallbacksWrapper>
      <ConfirmDialog
        open={open}
        onClose={onClose}
        aria-labelledby='company-financials-export-dialog-title'
        title='Export Historical Financials'
        onConfirm={onConfirmExport}
        footerButtonsProps={{
          confirmButtonText: 'Export to Excel',
          onConfirmDisabled: isACheckboxChecked,
        }}
      >
        <Stack direction='column' gap='0.25rem' justifyContent={'center'}>
          <Typography>Period</Typography>
          <ToggleFrequencyButtons frequency={frequency!} setFrequency={setFrequency} />
        </Stack>

        <Divider
          sx={{
            my: '1.75rem',
          }}
        />
        <Stack gap='1rem'>
          <Stack direction='row' gap='0.5rem'>
            <Checkbox checked={checkedByPeriod} onChange={onChangeSelectedPeriod} />
            <SelectPeriodDropdown
              period={frequency!}
              onChange={onChangeSelectedAmountOfPeriods}
              selectedAmountOfPeriodsIdx={selectedAmountOfPeriodsIdx}
            />
          </Stack>
          <Stack direction='row' gap='0.5rem' alignItems={'center'}>
            <Checkbox checked={checkedByAllHistorical} onChange={onChangeSelectedAllHistorical} />
            <Typography>All historical data</Typography>
          </Stack>
        </Stack>
      </ConfirmDialog>
      <div
        style={{
          // Basically we hide this table to make it only available for export
          display: 'none',
        }}
      >
        <AgTable
          columnDefs={columnDefs}
          onGridReady={onGridReady}
          rowData={rowData}
          rowGroupPanelShow='always'
          groupDefaultExpanded={-1}
          suppressMovableColumns={true}
          suppressRowDrag={true}
          defaultExcelExportParams={defaultExportParams}
          defaultCsvExportParams={defaultExportParams}
        ></AgTable>
      </div>
    </GenericFallbacksWrapper>
  );
}
